/**
 * Phase-1 packaging — current-session entitlement summary for FE nav/route guards.
 *
 * Fetches GET /api/v1/me/entitlements and shares the result in a module-level cache.
 *
 * ROBUSTNESS (the bug this fixes): the previous version cached the FIRST response
 * even when it was a transient 403 (auth token not attached yet during app boot) or
 * 503 (backend mid-restart), permanently locking the cache to OBSERVE/usable=null so
 * gating silently never engaged and never recovered. Now we cache ONLY a valid
 * success, and on any failure we keep RETRYING (every ~1s) until a real summary
 * arrives — so a transient hiccup self-heals into real gating.
 *
 * FAIL-OPEN while unresolved: until a valid summary loads, `usable === null` so every
 * module is treated as usable (nothing hidden) — no regression. Gating only engages
 * once a real summary is in hand AND the customer's mode is ENFORCE.
 */
import { useEffect, useState } from 'react';
import * as api from '../services/api';

let _cache = null; // set ONLY on a valid success

export function clearEntitlementsCache() { _cache = null; }

/** Normalize apiFetch's success/{data}/error shapes into a summary, or null on failure. */
function parseSummary(res) {
  if (!res || res.ok === false) return null;          // explicit apiFetch error object
  const d = res && res.data ? res.data : res;
  const valid = d && (Array.isArray(d.usable) || typeof d.enforcementMode === 'string');
  if (!valid) return null;                            // 4xx/5xx body, HTML, etc.
  return {
    usable: new Set(Array.isArray(d.usable) ? d.usable : []),
    mode: typeof d.enforcementMode === 'string' ? d.enforcementMode : 'OBSERVE',
    loaded: true,
  };
}

export function useEntitlements() {
  const [state, setState] = useState(_cache || { usable: null, mode: 'OBSERVE', loaded: false });

  useEffect(() => {
    if (_cache) { setState(_cache); return undefined; }
    let cancelled = false;
    let tries = 0;

    const attempt = () => {
      if (cancelled) return;
      if (_cache) { setState(_cache); return; }       // another instance already succeeded
      tries += 1;
      api.getMyEntitlements()
        .then((res) => {
          const parsed = parseSummary(res);
          if (!parsed) throw new Error('me/entitlements: transient/invalid');
          _cache = parsed;                            // cache ONLY on success
          if (!cancelled) setState(parsed);
        })
        .catch(() => {
          if (cancelled) return;
          // Fail-open for now (show everything) but KEEP retrying so a transient
          // 403/503 on boot doesn't permanently disable gating.
          setState((s) => ({ ...s, loaded: true }));
          if (tries < 10) setTimeout(attempt, 1000);
        });
    };
    attempt();
    return () => { cancelled = true; };
  }, []);

  // usable === null => unknown/fail-open => allow everything. `key` may be a single
  // ModuleKey or an array (any-of, e.g. /agents needs AGENT_GOV OR EXT_AGENT_GOV).
  const isUsable = (key) => {
    if (!key) return true;
    if (state.usable === null) return true;
    const keys = Array.isArray(key) ? key : [key];
    return keys.some((k) => k === 'CORE' || state.usable.has(k));
  };

  return {
    usable: state.usable,
    mode: state.mode,
    enforcing: state.mode === 'ENFORCE',
    loaded: state.loaded,
    isUsable,
  };
}
